/**
 * Quran.com QDC chapter audio timings (word segments) for karaoke-style sync.
 * Segment timestamps are absolute ms on the full chapter timeline.
 */

const cache = new Map();
const inflight = new Map();

/**
 * @typedef {{ wordPosition: number, timestampFrom: number, timestampTo: number }} WordSegment
 * @typedef {{ verseKey: string, timestampFrom: number, timestampTo: number, durationMs: number|null, segments: WordSegment[] }} VerseTiming
 * @typedef {{ audioUrl: string|null, durationMs: number|null, verseTimings: VerseTiming[] }} ChapterAudioTimings
 */

/**
 * @param {number|string} recitationId
 * @param {number|string} surahId
 * @returns {Promise<ChapterAudioTimings|null>}
 */
export async function fetchChapterAudioTimings(recitationId, surahId) {
  const rid = Number(recitationId);
  const sid = Number(surahId);
  if (!Number.isFinite(rid) || !Number.isFinite(sid) || sid < 1) return null;

  const key = `${rid}:${sid}`;
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);

  const promise = (async () => {
    const url =
      `https://api.qurancdn.com/api/qdc/audio/reciters/${rid}/audio_files` +
      `?chapter=${sid}&segments=true`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`QDC timings HTTP ${res.status}`);
    const data = await res.json();
    const file = data?.audio_files?.[0];
    if (!file) return null;

    /** @type {ChapterAudioTimings} */
    const parsed = {
      audioUrl: file.audio_url || null,
      durationMs: typeof file.duration === 'number' ? file.duration : null,
      verseTimings: (file.verse_timings || []).map((v) => ({
        verseKey: String(v.verse_key),
        timestampFrom: Number(v.timestamp_from) || 0,
        timestampTo: Number(v.timestamp_to) || 0,
        durationMs: typeof v.duration === 'number' ? v.duration : null,
        segments: (v.segments || [])
          .filter((seg) => Array.isArray(seg) && seg.length >= 3)
          .map((seg) => ({
            wordPosition: Number(seg[0]),
            timestampFrom: Number(seg[1]),
            timestampTo: Number(seg[2]),
          })),
      })),
    };
    cache.set(key, parsed);
    return parsed;
  })()
    .catch((err) => {
      console.error('Failed to load chapter audio timings:', err);
      return null;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * Resolve active word location from absolute chapter position (ms).
 * @param {ChapterAudioTimings|null|undefined} timings
 * @param {number} positionMs
 * @param {string} [preferVerseKey] - when playing a single ayah file, restrict search
 * @returns {string|null} e.g. "1:1:2"
 */
export function resolveActiveWordLocation(timings, positionMs, preferVerseKey) {
  if (!timings?.verseTimings?.length || !Number.isFinite(positionMs)) return null;

  let verse = null;
  if (preferVerseKey) {
    verse = timings.verseTimings.find((v) => v.verseKey === preferVerseKey) || null;
  } else {
    verse =
      timings.verseTimings.find(
        (v) => positionMs >= v.timestampFrom && positionMs < v.timestampTo
      ) || null;
    // End of chapter: last verse window is inclusive on the right edge
    if (!verse && timings.verseTimings.length) {
      const last = timings.verseTimings[timings.verseTimings.length - 1];
      if (positionMs >= last.timestampFrom && positionMs <= last.timestampTo) {
        verse = last;
      }
    }
  }
  if (!verse?.segments?.length) return null;

  const segment = verse.segments.find(
    (s) => positionMs >= s.timestampFrom && positionMs <= s.timestampTo
  );
  if (!segment) return null;
  return `${verse.verseKey}:${segment.wordPosition}`;
}

/**
 * Map per-ayah audio currentTime (seconds) into chapter-absolute ms, then resolve word.
 * @param {ChapterAudioTimings|null|undefined} timings
 * @param {string} ayahKey e.g. "2:255"
 * @param {number} currentTimeSec
 * @returns {string|null}
 */
export function resolveWordFromAyahPlayback(timings, ayahKey, currentTimeSec) {
  if (!timings || !ayahKey || !Number.isFinite(currentTimeSec)) return null;
  const verse = timings.verseTimings.find((v) => v.verseKey === ayahKey);
  if (!verse) return null;
  const positionMs = verse.timestampFrom + currentTimeSec * 1000;
  return resolveActiveWordLocation(timings, positionMs, ayahKey);
}

/**
 * Clear cached timings (e.g. after reciter change globally).
 */
export function clearChapterAudioTimingsCache() {
  cache.clear();
}
